import React from 'react';
import { Paper, Typography, List, IconButton, Box, Divider } from '@mui/material';
import { useDrop } from 'react-dnd';
import EditIcon from '@mui/icons-material/Edit';
import PatientItem from './PatientItem';
import { Patient } from "../../interfaces/Patient";
import { useTheme } from '@mui/material/styles';
import { t } from 'i18next';

interface CohortPatientsProps {
    patients: Patient[];
    selectedCohortName: string;
    cohortContact: string;
    handleDropToCohort: (patient: Patient) => void;
    handleRemoveFromCohort: (patient: Patient) => void;
    isAdmin: boolean;
    handleEditCohort: () => void;
    allowDragAndDrop: boolean;
}

const CohortPatients: React.FC<CohortPatientsProps> = ({
    patients,
    allowDragAndDrop,
    cohortContact,
    selectedCohortName,
    handleDropToCohort,
    handleRemoveFromCohort,
    isAdmin,
    handleEditCohort,
}) => {
    const theme = useTheme();

    const [{ isOver }, drop] = useDrop(
        () => ({
            accept: allowDragAndDrop ? 'PATIENT' : [],
            drop: allowDragAndDrop
                ? (item: Patient) => {
                      handleDropToCohort(item);
                  }
                : undefined,
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
            }),
        }),
        [allowDragAndDrop, handleDropToCohort]
    );

    // Sort patients by name in a case-insensitive manner
    const sortedPatients = [...patients].sort((a, b) => {
        const nameA = `${a.firstname || ''} ${a.lastname || ''}`.toLowerCase();
        const nameB = `${b.firstname || ''} ${b.lastname || ''}`.toLowerCase();
        return nameA.localeCompare(nameB);
    });

    return (
        <Paper
            elevation={3}
            sx={{
                padding: 2,
                backgroundColor: isOver ? theme.palette.action.hover : theme.palette.background.paper,
                color: theme.palette.text.primary,
            }}
            ref={drop}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                <Typography variant="h6">
                    {selectedCohortName ? `${selectedCohortName} (${patients.length})` : t("select_cohort")}
                </Typography>
                {isAdmin && selectedCohortName && (
                    <IconButton onClick={handleEditCohort} size="small">
                        <EditIcon />
                    </IconButton>
                )}
            </Box>
            {cohortContact && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t("contact")}: {cohortContact}
                </Typography>
            )}
            <Divider sx={{ marginBottom: 2 }} />
            {selectedCohortName ? (
                <List sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    {sortedPatients.map((patient) => (
                        <PatientItem
                            key={patient.patient_id}
                            patient={patient}
                            onRemove={handleRemoveFromCohort}
                            onClick={() => {}} // Empty function for now
                        />
                    ))}
                </List>
            ) : (
                <Typography variant="body2" color="textSecondary">
                    {t("select_cohort_to_view_patients")}
                </Typography>
            )}
        </Paper>
    );
};

export default CohortPatients;